/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import Button from "@shared/v2/Button";
import TextInput from "@shared/v2/TextInput";
import Typography from "@shared/v2/Typography";
import { useEffect, useRef, useState } from "react";
import useGooglePlaces from "./hooks/useGooglePlaces";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import Alert from "@shared/v2/Alert";
import TextButton from "@shared/v2/TextButton";

interface Chargify {
  load: (options: object) => void;
  token: (
    element: HTMLElement | null,
    successCallback: (token: string) => void,
    errorCallback: (error: object) => void,
  ) => void;
}

declare global {
  interface Window {
    Chargify: new () => Chargify;
  }
}

interface SalesFormProps {
  CHARGIFY_SITE: string;
  CHARGIFY_JS_PUBLIC_KEY: string;
}

const SalesForm = ({ CHARGIFY_SITE, CHARGIFY_JS_PUBLIC_KEY }: SalesFormProps) => {
  const addressInputRef = useRef<HTMLInputElement>(null);
  const [chargify, setChargify] = useState<Chargify>(null);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  useGooglePlaces(addressInputRef, (data) => {
    (document.querySelector("input[name='address']") as HTMLInputElement).value = data.address || "";
    (document.querySelector("input[name='address_2']") as HTMLInputElement).value = data.address2 || "";
    (document.querySelector("input[name='city']") as HTMLInputElement).value = data.city || "";
    (document.querySelector("input[name='state']") as HTMLInputElement).value = data.state || "";
    (document.querySelector("input[name='zip']") as HTMLInputElement).value = data.zipCode || "";
  });

  useEffect(() => {
    const script = document.getElementById("chargifyjs");
    if (!script) {
      const newScript = document.createElement("script");
      newScript.id = "chargifyjs";
      newScript.src = "https://js.chargify.com/latest/chargify.js";
      newScript.addEventListener("load", () => {
        setChargify(new window.Chargify());
      });
      document.head.appendChild(newScript);
    }
  }, []);

  useEffect(() => {
    if (!chargify || status !== "idle") return;
    chargify.load({
      selector: "#chargify",
      publicKey: CHARGIFY_JS_PUBLIC_KEY,
      type: "card",
      serverHost: CHARGIFY_SITE,
      fields: {
        number: { placeholder: "0000 0000 0000 0000" },
        cvv: { placeholder: "123" },
        month: { placeholder: "MM" },
        year: { placeholder: "YYYY" },
      },
    });
  }, [chargify, status]);

  if (status === "success") {
    return (
      <div className="tw-flex tw-flex-col tw-gap-[24px] tw-p-[24px]">
        <Alert variant="success" text="Thank you! Your information has been securely submitted." />
        {/* @ts-expect-error props */}
        <Button className="tw-self-center" size="medium" onClick={() => setStatus("idle")}>
          Reset
        </Button>
      </div>
    );
  }

  return (
    <form
      className="tw-h-full tw-overflow-hidden tw-flex tw-flex-col tw-gap-[12px] tw-p-[12px] tw-w-full tw-max-w-[640px]"
      onSubmit={(e) => {
        e.preventDefault();
        const form = e.currentTarget;
        setStatus("submitting");
        chargify.token(
          document.getElementById("chargify"),
          (token) => {
            const formData = { ...Object.fromEntries(new FormData(form)), paymentToken: token };
            caseTransformingAxios
              .post(`/place_support/sales_intake`, formData)
              .then(() => {
                setStatus("success");
              })
              .catch((err: { response?: { data?: { errors?: string } } }) => {
                setStatus("error");
                setErrorMessage(
                  err.response?.data?.errors ||
                    "There was an issue submitting your payment information. Please check your details and try again.",
                );
              });
          },
          (data: { errors?: string }) => {
            setStatus("error");
            setErrorMessage(
              data?.errors ||
                "There was an issue submitting your payment information. Please check your details and try again.",
            );
          },
        );
      }}
    >
      <div>
        {/* @ts-expect-error props */}
        <TextButton schema="sentence" size="medium" onClick={() => window.history.back()}>
          Back
        </TextButton>
        {/* @ts-expect-error props */}
        <Typography variant="headline1">Sales Contact & Credit Intake Form</Typography>
      </div>
      <p>Complete the form below to securely set-up the contact's billing details.</p>
      <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-[24px] tw-py-[12px] tw-overflow-auto">
        {/* @ts-expect-error props */}
        <Typography
          variant="headline3"
          className="tw-pb-[8px] tw-border-solid tw-border-[1px] tw-border-gray-10 tw-border-t-0 tw-border-x-0"
        >
          Contact Information
        </Typography>
        <div className="tw-flex tw-gap-[24px]">
          {/* @ts-expect-error props */}
          <TextInput containerClassName="tw-flex-1" label="First Name" name="firstName" isRequired required />
          {/* @ts-expect-error props */}
          <TextInput containerClassName="tw-flex-1" label="Last Name" name="lastName" isRequired required />
        </div>
        {/* @ts-expect-error props */}
        <TextInput label="Email Address" name="email" type="email" isRequired required />

        <div className="tw-flex tw-gap-[24px]">
          <TextInput
            // @ts-expect-error props
            containerClassName="tw-flex-1"
            placeholder="(123) 456-7890"
            label="Phone Number"
            name="phone"
            type="tel"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.currentTarget.value;
              const formattedValue = value
                .replace(/\D/g, "")
                .replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3")
                .slice(0, 14);
              e.currentTarget.value = formattedValue;
            }}
            pattern="\(\d{3}\) \d{3}-\d{4}"
            isRequired
            required
          />
          <TextInput
            // @ts-expect-error props
            containerClassName="tw-flex-1"
            label="Company Name"
            name="organization"
            isRequired
            required
          />
        </div>

        {/* @ts-expect-error props */}
        <Typography
          variant="headline3"
          className="tw-pb-[8px] tw-border-solid tw-border-[1px] tw-border-gray-10 tw-border-t-0 tw-border-x-0"
        >
          Secure Payment Information
        </Typography>
        <div id="chargify" />

        {/* @ts-expect-error props */}
        <Typography
          variant="headline3"
          className="tw-pb-[8px] tw-border-solid tw-border-[1px] tw-border-gray-10 tw-border-t-0 tw-border-x-0"
        >
          Billing Address
        </Typography>
        {/* @ts-expect-error props */}
        <TextInput label="Street Address" name="address" isRequired required ref={addressInputRef} />
        {/* @ts-expect-error props */}
        <TextInput label="Street Address 2" name="address_2" />

        <div className="tw-flex tw-gap-[24px]">
          {/* @ts-expect-error props */}
          <TextInput containerClassName="tw-flex-1" label="City" name="city" isRequired required />
          {/* @ts-expect-error props */}
          <TextInput containerClassName="tw-flex-1" label="State/Province" name="state" isRequired required />
        </div>
        <div className="tw-flex tw-gap-[24px]">
          {/* @ts-expect-error props */}
          <TextInput containerClassName="tw-flex-1" label="Postal Code" name="zip" isRequired required />
          <span className="tw-flex-1" />
        </div>
      </div>

      {status === "error" && <Alert variant="error" text={errorMessage} />}

      <div>
        {/* @ts-expect-error props */}
        <Button type="submit" size="medium">
          Submit
        </Button>
      </div>
    </form>
  );
};

export default SalesForm;
